import {
  Component,
  computed,
  inject,
  signal,
  HostListener,
  ElementRef,
} from '@angular/core';

import { Router } from '@angular/router';
import { AuthStore } from '../../../store/auth.store';
import {
  ServicePermissionUtils,
  SERVICE_KEY,
  PERMISSIONS,
} from '@sandbox-platform/shared-ui';

interface TicketingOption {
  title: string;
  description: string;
  route: string;
  iconPath: string;
  permission: string;
}

@Component({
  standalone: true,
  imports: [],
  selector: 'app-ticketing-navigation-card',
  template: `
    <div class="relative">
      <button
        (click)="handleClick()"
        class="group bg-white p-6 rounded-lg shadow-md border border-gray-200 hover:shadow-md hover:border-orange-300 transition-all duration-200 text-left w-full"
      >
        <div class="flex items-center mb-4">
          <div
            class="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors duration-200"
          >
            <svg
              class="w-6 h-6 text-orange-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
              />
            </svg>
          </div>
        </div>
        <h3 class="text-lg font-semibold text-gray-800 mb-2">Ticketing</h3>
        <p class="text-sm text-gray-500">
          Manage support tickets, track issues, and configure the ticketing
          system
        </p>
      </button>

      <!-- Dropdown Menu -->
      @if (showDropdown()) {
      <div
        class="absolute top-full left-0 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden"
      >
        @for (option of availableOptions(); track option.route) {
        <button
          (click)="navigateTo(option.route)"
          class="w-full px-6 py-4 text-left hover:bg-orange-50 transition-colors duration-150 border-b border-gray-100 last:border-b-0"
        >
          <div class="flex items-start gap-4">
            <div
              class="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0"
            >
              <svg
                class="w-5 h-5 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  [attr.d]="option.iconPath"
                />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <h4 class="text-base font-semibold text-gray-800 mb-1">
                {{ option.title }}
              </h4>
              <p class="text-sm text-gray-500">{{ option.description }}</p>
            </div>
          </div>
        </button>
        }
      </div>
      }
    </div>
  `,
})
export class TicketingNavigationCardComponent {
  private router = inject(Router);
  public authStore = inject(AuthStore);
  private elementRef = inject(ElementRef);

  showDropdown = signal(false);

  private readonly ticketingOptions: TicketingOption[] = [
    {
      title: 'Ticketing',
      description:
        'Manage support tickets, track issues, and communicate with support team',
      route: '/ticketing-app',
      iconPath:
        'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z',
      permission: PERMISSIONS.TICKETING.TICKETS_READ,
    },
    {
      title: 'Ticketing Support',
      description:
        'Support agent dashboard for managing ticket queue and assignments',
      route: '/ticketing-support',
      iconPath:
        'M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z',
      permission: PERMISSIONS.TICKETING.ASSIGNMENTS_READ,
    },
    {
      title: 'Ticketing Admin',
      description:
        'Configure ticketing system, manage categories, and view analytics',
      route: '/ticketing-admin',
      iconPath:
        'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
      permission: PERMISSIONS.TICKETING.CATEGORIES_READ,
    },
  ];

  readonly availableOptions = computed<TicketingOption[]>(() => {
    const user = this.authStore.user();
    return this.ticketingOptions.filter((option) =>
      ServicePermissionUtils.hasPermission(
        user,
        SERVICE_KEY.TICKETING,
        option.permission
      )
    );
  });

  handleClick(): void {
    const options = this.availableOptions();

    // If only one option is available, route directly
    if (options.length === 1) {
      this.navigateTo(options[0].route);
      return;
    }

    // If multiple options, toggle dropdown
    this.showDropdown.update((value) => !value);
  }

  navigateTo(route: string): void {
    this.showDropdown.set(false);

    if (route === '/ticketing-app') {
      window.location.href = '/ticketing-app/dashboard';
    } else if (route === '/ticketing-support') {
      window.location.href = '/ticketing-support/dashboard';
    } else if (route === '/ticketing-admin') {
      window.location.href = '/ticketing-admin/dashboard';
    } else {
      this.router.navigate([route]);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.showDropdown() &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.showDropdown.set(false);
    }
  }
}

